/**
 * Session mode, chosen at the start (S-21, owner ruling 2026-10-14).
 *
 * One three-way choice — Cycle / App / Stand-alone — pre-selected with the
 * app's best guess (a cycle and track picked → Cycle; otherwise App), and
 * limited to the modes the workflow allows (allowed_session_modes).
 *
 * A mode the workflow does not allow is not offered; the line under the
 * choice says why it is missing. Cycle needs a cycle and a track: when none
 * is picked, the Cycle option says so and offers to pick one.
 */
import { SESSION_CONTEXT_MODES, SESSION_CONTEXT_MODE_LABELS, type SessionContextMode } from '@/types/prioritization/workflow';


const DESCRIPTIONS: Record<SessionContextMode, string> = {
  cycle_linked: "Ranks a track's items; the result can go to the cycle.",
  app_linked: 'Uses workspace items, not tied to a cycle.',
  isolated: 'Type items in directly; nothing outside the session changes.',
};

export interface SessionModeChooserProps {
  value: SessionContextMode;
  onChange: (mode: SessionContextMode) => void;
  /** Modes the workflow allows. */
  allowed: readonly SessionContextMode[];
  /** A cycle and track are picked, so Cycle can be chosen. */
  hasCycleAndTrack: boolean;
  /** Go pick a cycle and track (shown on the Cycle option when none is picked). */
  onPickCycle?: () => void;
}

export function SessionModeChooser({ value, onChange, allowed, hasCycleAndTrack, onPickCycle }: SessionModeChooserProps) {
  const shown = SESSION_CONTEXT_MODES.filter((m) => allowed.includes(m));
  const hidden = SESSION_CONTEXT_MODES.filter((m) => !allowed.includes(m));

  return (
    <fieldset data-testid="session-mode-chooser">
      <legend className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">Session mode</legend>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup">
        {shown.map((mode) => {
          const needsCycle = mode === 'cycle_linked' && !hasCycleAndTrack;
          const selected = value === mode && !needsCycle;
          return (
            <div
              key={mode}
              className={`border px-3 py-2.5 text-left ${
                selected ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] bg-[var(--bg-raised)]'
              }`}
            >
              {needsCycle ? (
                <>
                  <p className="text-sm font-medium text-[var(--text-pri)]">{SESSION_CONTEXT_MODE_LABELS[mode]}</p>
                  <p className="text-xs text-[var(--text-ter)] mt-0.5">Needs a cycle and a track.</p>
                  {onPickCycle && (
                    <button type="button" onClick={onPickCycle} className="mt-1 text-xs underline text-[var(--text-sec)]">
                      Pick one
                    </button>
                  )}
                </>
              ) : (
                <label className="block cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="session-mode"
                      value={mode}
                      checked={selected}
                      onChange={() => onChange(mode)}
                    />
                    <span className="text-sm font-medium text-[var(--text-pri)]">{SESSION_CONTEXT_MODE_LABELS[mode]}</span>
                  </span>
                  <span className="block text-xs text-[var(--text-ter)] mt-0.5">{DESCRIPTIONS[mode]}</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
      {hidden.length > 0 && (
        <p className="text-xs text-[var(--text-ter)] mt-1.5">
          The workflow does not allow {hidden.map((m) => SESSION_CONTEXT_MODE_LABELS[m]).join(' or ')} sessions here.
        </p>
      )}
    </fieldset>
  );
}
